// ── Scoreboard Stats ──
// Helper module for enhanced scoreboard statistics:
// ADR, HS%, flash assists, utility damage, MVPs, sorting, team economy.

// ── Grenade weapon IDs that count as utility ──
const UTILITY_WEAPONS = new Set(['hegrenade', 'molotov', 'incendiary', 'flashbang', 'smoke']);

// ── Types ──

export interface EnhancedScoreboardEntry {
  name: string;
  kills: number;
  deaths: number;
  assists: number;
  ping: number;
  isLocal: boolean;
  team: number;       // 0=unassigned, 1=T, 2=CT
  adr: number;        // Average Damage per Round
  hsPercent: number;   // Headshot kill percentage (0-100)
  flashAssists: number;
  utilityDamage: number;
  mvps: number;
  money: number;
  weapon: string;      // Current weapon ID held by player
  // UI-H5: highlight-reel achievements rendered as compact badges.
  /** Number of 1vN clutches won this match. */
  clutches?: number;
  /** Number of aces (5+ kills in a single round) this match. */
  aces?: number;
  /** Longest kill shot distance, in metres. 0 means "no recorded shot". */
  longestShot?: number;
}

export interface PlayerTrackedStats {
  totalDamage: number;
  headshotKills: number;
  flashAssists: number;
  utilityDamage: number;
  mvps: number;
  // UI-H5: highlight-reel counters fed by the scoreboard render path.
  clutches: number;
  aces: number;
  longestShot: number;
}

// ── Pure calculation helpers ──

/** Calculate Average Damage per Round. Returns 0 if rounds is 0. Rounds to 1 decimal. */
export function calculateADR(totalDamage: number, rounds: number): number {
  if (rounds <= 0) return 0;
  return Math.round((totalDamage / rounds) * 10) / 10;
}

/** Calculate headshot kill percentage (0-100). Returns 0 if totalKills is 0. */
export function calculateHSPercent(headshotKills: number, totalKills: number): number {
  if (totalKills <= 0) return 0;
  return Math.round((headshotKills / totalKills) * 100);
}

/** Returns a CSS color string for an ADR value: green (>80), yellow (50-80), red (<50). */
export function adrColor(adr: number): string {
  if (adr > 80) return '#4caf50';
  if (adr >= 50) return '#ff9800';
  return '#f44336';
}

/** Sort entries by kills descending, then by ADR descending as tiebreaker. */
export function sortScoreboardEntries(entries: EnhancedScoreboardEntry[]): EnhancedScoreboardEntry[] {
  return [...entries].sort((a, b) => {
    if (b.kills !== a.kills) return b.kills - a.kills;
    return b.adr - a.adr;
  });
}

/** Sum the money of all entries (team economy total). */
export function teamEconomyTotal(entries: EnhancedScoreboardEntry[]): number {
  return entries.reduce((sum, e) => sum + e.money, 0);
}

// ── ScoreboardStats class ──
// Tracks per-player statistics that the server doesn't send in snapshots.
// Fed by game events (kill, damage, flash_blind, roundEnd).

export class ScoreboardStats {
  private playerStats = new Map<number, PlayerTrackedStats>();
  private roundsPlayed = 0;

  /** Get stats for a player, auto-creating defaults if not yet tracked. */
  getPlayerStats(playerId: number): PlayerTrackedStats {
    let s = this.playerStats.get(playerId);
    if (!s) {
      s = {
        totalDamage: 0,
        headshotKills: 0,
        flashAssists: 0,
        utilityDamage: 0,
        mvps: 0,
        clutches: 0,
        aces: 0,
        longestShot: 0,
      };
      this.playerStats.set(playerId, s);
    }
    return s;
  }

  /** UI-H5: Record a 1vN clutch win for a player. */
  recordClutch(playerId: number): void {
    this.getPlayerStats(playerId).clutches++;
  }

  /** UI-H5: Record an ace (5+ kills in a single round) for a player. */
  recordAce(playerId: number): void {
    this.getPlayerStats(playerId).aces++;
  }

  /**
   * UI-H5: Record a kill-shot distance. Only updates the player's
   * `longestShot` when the new value beats the prior record.
   */
  recordKillShotDistance(playerId: number, distance: number): void {
    const s = this.getPlayerStats(playerId);
    if (distance > s.longestShot) {
      s.longestShot = distance;
    }
  }

  /** Record a damage event. Tracks total damage and utility damage. */
  recordDamage(attackerId: number, _victimId: number, damage: number, _bodyPart: string, weaponId: string): void {
    const s = this.getPlayerStats(attackerId);
    s.totalDamage += damage;
    if (UTILITY_WEAPONS.has(weaponId)) {
      s.utilityDamage += damage;
    }
  }

  /** Record a kill event. Tracks headshot kills. */
  recordKill(killerId: number, _victimId: number, bodyPart: string, _weaponId: string): void {
    const s = this.getPlayerStats(killerId);
    if (bodyPart === 'head') {
      s.headshotKills++;
    }
  }

  /** Record a flash assist (enemy killed while blinded by this player's flash). */
  recordFlashAssist(playerId: number): void {
    this.getPlayerStats(playerId).flashAssists++;
  }

  /** Record an MVP award for a player. */
  recordMVP(playerId: number): void {
    this.getPlayerStats(playerId).mvps++;
  }

  /** Record end of a round (increments round counter). */
  recordRoundEnd(): void {
    this.roundsPlayed++;
  }

  /** Get current rounds played count. */
  getRoundsPlayed(): number {
    return this.roundsPlayed;
  }

  /** Calculate ADR for a player using tracked damage and rounds. */
  getADR(playerId: number): number {
    const s = this.getPlayerStats(playerId);
    return calculateADR(s.totalDamage, this.roundsPlayed);
  }

  /** Calculate HS% for a player. Needs totalKills passed in (from server snapshot). */
  getHSPercent(playerId: number, totalKills: number): number {
    const s = this.getPlayerStats(playerId);
    return calculateHSPercent(s.headshotKills, totalKills);
  }

  /** Reset all tracked stats (e.g., on match restart). */
  reset(): void {
    this.playerStats.clear();
    this.roundsPlayed = 0;
  }
}
